import type { MessagingMessage } from '@gokrew/models/messaging';

/** Sort sequence strings without losing precision to JavaScript numbers. */
export const compareMessagingSequence = (left: string, right: string) => {
  const a = left.replace(/^0+/, '') || '0';
  const b = right.replace(/^0+/, '') || '0';
  return a.length === b.length ? a.localeCompare(b) : a.length - b.length;
};

/** REST history, realtime and idempotent acknowledgements may overlap. */
export const mergeMessagingMessages = (rows: MessagingMessage[]) => {
  const unique = new Map<string, MessagingMessage>();
  rows.forEach((row) => unique.set(row.id, row));
  return [...unique.values()].sort((a, b) =>
    compareMessagingSequence(a.sequence, b.sequence)
  );
};

/** A retry always keeps the original key and text together. */
export const messagingDraft = (
  text: string,
  previous?: { text: string; clientMessageId: string }
) =>
  previous?.text === text
    ? previous
    : { text, clientMessageId: crypto.randomUUID() };

/** A replay owns one session generation. Disposed sessions cannot publish late responses. */
export const createMessagingReplay = (options: {
  fetch: (
    after: string
  ) => Promise<import('@gokrew/models/messaging').MessagingSyncResult>;
  apply: (event: import('@gokrew/models/messaging').MessagingEvent) => void;
  persist: (cursor: string) => void;
  initialCursor?: string | null;
}) => {
  let cursor = options.initialCursor ?? null;
  let disposed = false;
  let running = false;
  let again = false;
  let generation = 0;
  return {
    initialize(value: string) {
      if (!disposed) cursor ??= value;
    },
    activate() {
      disposed = false;
      running = false;
      again = false;
      generation += 1;
    },
    dispose() {
      disposed = true;
      generation += 1;
    },
    get active() {
      return !disposed;
    },
    async run() {
      if (disposed || !cursor) return;
      if (running) {
        again = true;
        return;
      }
      running = true;
      const ownGeneration = generation;
      const active = () => !disposed && ownGeneration === generation;
      try {
        do {
          again = false;
          let more = true;
          while (more && active()) {
            const page = await options.fetch(cursor!);
            if (!active()) return;
            page.events.forEach((event) => {
              if (active()) options.apply(event);
            });
            if (!active()) return;
            cursor = page.nextCursor;
            options.persist(cursor);
            more = page.hasMore;
          }
        } while (again && active());
      } finally {
        if (ownGeneration === generation) running = false;
      }
    },
  };
};

export interface MessagingComposerSnapshot {
  text: string;
  pending?: { text: string; clientMessageId: string };
}

const draftKey = (scope: string, id: string) =>
  `messaging-draft:${scope}:${id}`;
export const readMessagingDraft = (
  scope: string,
  id: string
): MessagingComposerSnapshot => {
  try {
    const value = JSON.parse(
      sessionStorage.getItem(draftKey(scope, id)) || 'null'
    );
    if (!value || typeof value.text !== 'string') return { text: '' };
    const pending = value.pending;
    return {
      text: value.text.slice(0, 4000),
      ...(pending &&
      typeof pending.text === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        pending.clientMessageId
      )
        ? {
            pending: {
              text: pending.text.slice(0, 4000),
              clientMessageId: pending.clientMessageId,
            },
          }
        : {}),
    };
  } catch {
    return { text: '' };
  }
};
export const saveMessagingDraft = (
  scope: string,
  id: string,
  snapshot: MessagingComposerSnapshot
): boolean => {
  try {
    if (!snapshot.text && !snapshot.pending)
      sessionStorage.removeItem(draftKey(scope, id));
    else sessionStorage.setItem(draftKey(scope, id), JSON.stringify(snapshot));
    return true;
  } catch {
    return false;
  }
};

/** Another account in this tab must never inherit the previous person's drafts. */
export const isolateMessagingDrafts = (
  portal: string,
  userId: string
): void => {
  try {
    const identityKey = `messaging-draft-user:${portal}`;
    const previous = sessionStorage.getItem(identityKey);
    if (previous && previous !== userId) {
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith(`messaging-draft:${portal}:`))
        .forEach((key) => sessionStorage.removeItem(key));
    }
    sessionStorage.setItem(identityKey, userId);
  } catch {
    /* Drafts still remain isolated by the complete session scope. */
  }
};

/** Only an explicit HTTP rejection establishes that a write was not accepted. */
export const isKnownMessagingRejection = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
};

/** One visible text line is sufficient even when a long bubble exceeds the viewport. */
export const messagingBubbleIsVisible = (
  visibleHeight: number,
  bubbleHeight: number,
  viewportHeight: number
) =>
  visibleHeight >=
  Math.min(24, Math.max(1, bubbleHeight), Math.max(1, viewportHeight));

/** Server-forced disconnects need an explicit, authenticated reconnect path. */
export const createMessagingReconnect = (options: {
  validate: () => Promise<boolean>;
  reconnect: () => void;
  isOnline: () => boolean;
}) => {
  let timer: number | undefined;
  let generation = 0;
  let attempts = 0;
  let disposed = false;
  let validating = false;
  const cancel = () => {
    generation += 1;
    if (timer !== undefined) window.clearTimeout(timer);
    timer = undefined;
    validating = false;
    attempts = 0;
  };
  const schedule = () => {
    if (
      disposed ||
      timer !== undefined ||
      validating ||
      !options.isOnline() ||
      attempts >= 8
    )
      return;
    const delay = Math.min(1000 * 2 ** attempts, 15_000);
    attempts += 1;
    timer = window.setTimeout(async () => {
      timer = undefined;
      const ownGeneration = generation;
      validating = true;
      let retry = false;
      try {
        const valid = await options.validate();
        if (disposed || generation !== ownGeneration || !options.isOnline())
          return;
        if (valid) options.reconnect();
        else retry = true;
      } catch {
        retry = true;
      } finally {
        if (generation === ownGeneration && !disposed) {
          validating = false;
          if (retry) schedule();
        }
      }
    }, delay);
  };
  return {
    schedule,
    cancel,
    dispose: () => {
      cancel();
      disposed = true;
    },
  };
};

export const latestMessagingSettings = (
  current: import('@gokrew/models/messaging').MessagingSettings | undefined,
  incoming: import('@gokrew/models/messaging').MessagingSettings
) =>
  current && compareMessagingSequence(current.version, incoming.version) >= 0
    ? current
    : incoming;
